// src/start/Door.tsx: the doorway shell of the pages homebuild serves on the builder's host before there is a board
// (docs/onsite/proto/proto.css "Doorway shell"): the builder's logo left (a link back to their site), on the right
// "Already started a board? Sign in" or "Back to {site}", and the phone number (a round call button on phone).
// The footer: the builder, their address and phone, Privacy, and "Powered by homebuild" once (on a phone, one line:
// the number, Privacy and "Powered by homebuild").
import type { ComponentChildren } from "preact";
import { joinParts } from "../../shared/format";
import { href, Link } from "../app/router";
import { siteHost, siteHref } from "../lib/site-link";
import { tenant } from "../state/session";
import { cx } from "../ui/cx";
import { Icon } from "../ui/Icon";
import "../styles/start.css";
import { Logo } from "../ui/Logo";

export { siteHost };

export function CallButton(props: { class?: string; showNumber?: boolean }) {
  const t = tenant.value;
  if (!t || !t.contact.phone?.trim()) return null;
  return (
    <a class={cx("dw-phone", props.class)} href={t.contact.phoneHref} aria-label={`Call ${t.contact.phone}`}>
      <Icon name="phone" size="sm" />
      {props.showNumber === false ? null : <span class="num">{t.contact.phone}</span>}
    </a>
  );
}

export function DoorTop(props: { right?: "signin" | "back" | "none"; children?: ComponentChildren }) {
  const t = tenant.value;
  if (!t) return null;
  const right = props.right ?? "signin";
  return (
    <header class="dw-top">
      <div class="dw-top-in">
        <a class="dw-logo" href={siteHref(t)} title={`Back to ${siteHost(t.contact.site)}`}>
          <Logo slot="door" />
        </a>
        <div class="dw-top-r">
          {props.children}
          {right === "signin" ? (
            <Link class="dw-signin" to={href("signin")}>
              <span class="long">
                Already started a board? <b>Sign in</b>
              </span>
              <span class="short">
                <b>Sign in</b>
              </span>
            </Link>
          ) : right === "back" ? (
            <a class="dw-back" href={siteHref(t)}>
              Back to {siteHost(t.contact.site)}
            </a>
          ) : null}
          <CallButton />
        </div>
      </div>
    </header>
  );
}

export function DoorFoot() {
  const t = tenant.value;
  if (!t) return null;
  // "Street, Suite A, Town, ST 12345" reads "Street, Town, ST" in one footer line.
  const address = t.contact.address
    .split(",")
    .map((p) => p.trim())
    .filter((p) => p && !/^(suite|ste\.?|unit|#)\s/i.test(p))
    .map((p) => p.replace(/\s+\d{5}(-\d{4})?$/, ""))
    .join(", ");
  return (
    <footer class="dw-foot">
      <div class="dw-foot-in">
        <span>
          {joinParts([t.name, address])}
          {t.contact.phone?.trim() ? (
            <>
              {joinParts([t.name, address]) ? " · " : ""}
              <a href={t.contact.phoneHref}>{t.contact.phone}</a>
            </>
          ) : null}
        </span>
        {/* The phone's one line: the number, Privacy, Powered by homebuild (start.css) */}
        {t.contact.phone?.trim() ? (
          <a class="df-phone" href={t.contact.phoneHref}>
            {t.contact.phone}
          </a>
        ) : null}
        {t.legal.privacyUrl ? <a href={t.legal.privacyUrl}>Privacy</a> : null}
        <span class="powered">Powered by homebuild</span>
      </div>
    </footer>
  );
}

export function Door(props: { screen: string; right?: "signin" | "back" | "none"; topExtra?: ComponentChildren; foot?: boolean; class?: string; children?: ComponentChildren }) {
  return (
    <div class={cx("hb-door", props.class)} data-screen={props.screen}>
      <DoorTop right={props.right}>{props.topExtra}</DoorTop>
      <main class="dw-main" id="main" tabIndex={-1}>
        {props.children}
      </main>
      {props.foot === false ? null : <DoorFoot />}
    </div>
  );
}
